import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from '@/app/services/api.service';
import { NotificationService } from '@/app/services/notification.service';
import { handleApiError } from '@/app/utils/api-error-handler';
import * as productAsset from '@/lib-backend/methods/productAsset';
import {
    ProductBaseArt,
    ProductBaseArtPropertyInput,
    ProductFinalizedArt,
    ProductFinalizedArtPropertyInput,
} from '@/lib-backend';

/**
 * The only product type that exists today: the per-site-template art overrides
 * of a game template. `ProductId` is the game template id; the backend keys a
 * base-art document by the triple (SiteTemplateId, ProductType, ProductId).
 */
export const GAME_TEMPLATE_PRODUCT_TYPE = 'GameTemplate';

/**
 * Well-known property names carried by an art entry's dynamic `Properties` list.
 * Base art exposes `icon`/`banner` plates; finalized variants additionally carry
 * the variant `name` (defaults to `GameTemplate.Title`) and an optional
 * `language` that the public `findProductFinalizedArt` filter keys on.
 */
export const ART_PROP = {
    Icon: 'icon',
    Banner: 'banner',
    Name: 'name',
    Language: 'language',
    /** Discriminator separating an `iconBanner` variant from a `pages` variant. */
    Group: 'group',
} as const;

/**
 * Finalized-art groups. Under one key `(siteTemplate, gameTemplate, name)` the
 * append-only variant list holds two kinds of variant that must never mix: an
 * `iconBanner` pair, or an ordered `pages` storyboard. The explicit `group`
 * property is the discriminator (more robust than sniffing which props exist).
 */
export const ART_GROUP = {
    IconBanner: 'iconBanner',
    Pages: 'pages',
} as const;
export type ArtGroup = (typeof ART_GROUP)[keyof typeof ART_GROUP];

/** Property name for the Nth (1-based) storyboard page of a `pages` variant. */
export function pageProp(n: number): string {
    return `page_${n}`;
}

/**
 * Classify a finalized-art variant. Reads the explicit `group` property; falls
 * back to sniffing (`page_1` present → pages) for legacy variants saved before
 * the discriminator existed.
 */
export function finalizedGroup(
    art: ProductFinalizedArt,
): ArtGroup {
    const explicit = readArtProperty(art, ART_PROP.Group);
    if (explicit === ART_GROUP.Pages || explicit === ART_GROUP.IconBanner) {
        return explicit;
    }
    return readArtProperty(art, pageProp(1)) !== null
        ? ART_GROUP.Pages
        : ART_GROUP.IconBanner;
}

/** Ordered page URLs of a `pages` variant (`page_1`, `page_2`, …). */
export function readPageUrls(art: ProductFinalizedArt): string[] {
    const urls: string[] = [];
    for (let n = 1; ; n++) {
        const url = readArtProperty(art, pageProp(n));
        if (url === null) break;
        urls.push(url);
    }
    return urls;
}

export type SetBaseArtOutcome = {
    result: productAsset.SetProductBaseArtResult;
    baseArt: ProductBaseArt | null;
};

export type UpdateBaseArtOutcome = {
    result: productAsset.UpdateProductBaseArtResult;
    baseArt: ProductBaseArt | null;
};

export type AddFinalizedArtOutcome = {
    result: productAsset.AddProductFinalizedArtResult;
    finalizedArt: ProductFinalizedArt | null;
};

/**
 * Read a single property value from an art entry, case-insensitively by name.
 * Returns null when the property is absent (e.g. a variant with no `language`).
 */
export function readArtProperty(
    art: ProductBaseArt | ProductFinalizedArt,
    name: string,
): string | null {
    const wanted = name.trim().toLowerCase();
    return (
        art.Properties.find((p) => p.Name.trim().toLowerCase() === wanted)
            ?.Value ?? null
    );
}

/**
 * Wraps the Admin `productAsset.*` methods. A base-art document is a flat set
 * of named art slots (`Name -> Value`, Value being a shared-content URL) that
 * lets a site template override how a game template renders inside it.
 */
@Injectable({ providedIn: 'root' })
export class ProductAssetService {
    private readonly api = inject(ApiService);
    private readonly router = inject(Router);
    private readonly notification = inject(NotificationService);

    /**
     * Load the base art for one (site template, game template) pair, or null
     * when none is stored yet. The list endpoint returns at most one item when
     * all three keys are supplied.
     */
    async getGameTemplateBaseArt(
        siteTemplateId: string,
        gameTemplateId: string,
    ): Promise<ProductBaseArt | null> {
        try {
            const resp = await this.api.client.call(
                productAsset.listProductBaseArt({
                    SiteTemplateId: siteTemplateId,
                    ProductType: GAME_TEMPLATE_PRODUCT_TYPE,
                    ProductId: gameTemplateId,
                }),
            );
            return resp.Items[0] ?? null;
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return null;
        }
    }

    /** Replace the full property set for a (site template, game template) pair. */
    async setGameTemplateBaseArt(
        siteTemplateId: string,
        gameTemplateId: string,
        properties: ProductBaseArtPropertyInput[],
    ): Promise<SetBaseArtOutcome> {
        try {
            const resp = await this.api.client.call(
                productAsset.setProductBaseArt({
                    SiteTemplateId: siteTemplateId,
                    ProductType: GAME_TEMPLATE_PRODUCT_TYPE,
                    ProductId: gameTemplateId,
                    Properties: properties,
                }),
            );
            return { result: resp.Result, baseArt: resp.BaseArt };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { result: 'Unknown', baseArt: null };
        }
    }

    /**
     * Upsert and/or clear individual base-art slots without a full replace.
     * `setProperties` are upserted by name; `removeNames` are cleared. This is
     * what the per-slot auto-save (select = save, deselect = save null) uses, so
     * concurrent edits across site templates never race on a read-merge-write.
     */
    async updateGameTemplateBaseArt(
        siteTemplateId: string,
        gameTemplateId: string,
        setProperties: ProductBaseArtPropertyInput[],
        removeNames: string[],
    ): Promise<UpdateBaseArtOutcome> {
        try {
            const resp = await this.api.client.call(
                productAsset.updateProductBaseArt({
                    SiteTemplateId: siteTemplateId,
                    ProductType: GAME_TEMPLATE_PRODUCT_TYPE,
                    ProductId: gameTemplateId,
                    SetProperties: setProperties,
                    RemoveNames: removeNames,
                }),
            );
            return { result: resp.Result, baseArt: resp.BaseArt };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { result: 'Unknown', baseArt: null };
        }
    }

    // ── Finalized art ───────────────────────────────────────────────────────
    // Unlike base art (one upserted plate per key), finalized art is
    // append-only: a key holds many variants and the operator picks one. The
    // Admin list endpoint has no filter — it returns every variant for the
    // (site template, game template) pair, narrowed client-side.

    /**
     * Every finalized-art variant stored for one (site template, game template)
     * pair, newest first. Empty when none have been generated yet.
     */
    async listGameTemplateFinalizedArt(
        siteTemplateId: string,
        gameTemplateId: string,
    ): Promise<ProductFinalizedArt[]> {
        try {
            const resp = await this.api.client.call(
                productAsset.listProductFinalizedArt({
                    SiteTemplateId: siteTemplateId,
                    ProductType: GAME_TEMPLATE_PRODUCT_TYPE,
                    ProductId: gameTemplateId,
                }),
            );
            return [...resp.Items].sort((a, b) =>
                b.CreatedAt.localeCompare(a.CreatedAt),
            );
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return [];
        }
    }

    /**
     * Append a new finalized-art variant. Never overwrites an existing one —
     * "regenerate" is just another call. The backend validates the site
     * template (`SiteTemplateNotFound`).
     */
    async addGameTemplateFinalizedArt(
        siteTemplateId: string,
        gameTemplateId: string,
        properties: ProductFinalizedArtPropertyInput[],
    ): Promise<AddFinalizedArtOutcome> {
        try {
            const resp = await this.api.client.call(
                productAsset.addProductFinalizedArt({
                    SiteTemplateId: siteTemplateId,
                    ProductType: GAME_TEMPLATE_PRODUCT_TYPE,
                    ProductId: gameTemplateId,
                    Properties: properties,
                }),
            );
            return { result: resp.Result, finalizedArt: resp.FinalizedArt };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { result: 'Unknown', finalizedArt: null };
        }
    }

    /** Remove a single finalized-art variant by its id. True on success. */
    async deleteFinalizedArt(id: string): Promise<boolean> {
        try {
            const resp = await this.api.client.call(
                productAsset.deleteProductFinalizedArt({ Id: id }),
            );
            return resp.Result === 'Ok';
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return false;
        }
    }
}
